import { BackofficeUser, Roles } from "../../../../types/BackofficeUser";
import { FormDefinition } from "../../../../types/ProductService/ProductService";
import * as stagesCase from "../../../Stages/StagesCase";
import { abstractLitigationStageFormDefinition } from "../abstractLitigationCase/stageFormDefinition";
import { FormElementPropsWithoutProduct } from "../../../../components/Case/CaseForm/FormElement";
import personFields from "../modules/personFields";
import { dateFieldDefault, instituteReferenceDefault } from "../../../validationRules";
import _ from "lodash";
import { overwriteFormElementField, trueFalseDefaultOptions } from "../formDefinitionFunctions";
import { userHasOneOfTheseRoles } from "../../../backofficeUserService";
import { ProductUnion } from "../../../../types/ProductUnion";
import { EmploymentContract } from "../../../../types/Products/EmploymentLaw/EmploymentContract";

export function stageFormDefinition(
  product: EmploymentContract,
  currentUser?: BackofficeUser
): ReturnType<FormDefinition<EmploymentContract>> {
  const employmentContractSpecificFields: FormElementPropsWithoutProduct<EmploymentContract>[] = [
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.employmentStartDate`,
      validators: dateFieldDefault,
      isHidden: ({ product }) => product.advisoryHandling === true,
    },
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.employmentEndDate`,
      validators: dateFieldDefault,
      isHidden: ({ product }) => !product.employmentEndDate,
    },
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.reasonsForDispute`,
      additionalProps: {
        multiple: true,
      },
      isDisabled: ({ product }) => product.stage >= stagesCase.STAGE_CHECKED,
      isHidden: ({ product }) => product.stage > stagesCase.STAGE_CHECKED,
      options: ({ product }) => [
        { value: "salary" },
        {
          value: "overtime",
          label:
            product.subjectOfDispute !== "money"
              ? "Überstunden / Freizeitausgleich"
              : "Finanzielle Ansprüche aus Überstunden",
        },
        {
          value: "vacation",
          label: product.subjectOfDispute !== "money" ? "Gewährung von Urlaub" : "Finanzielle Ansprüche aus Urlaub",
        },
        { value: "workCertificate" },
        { value: "parentalLeave" },
        { value: "partTime" },
        { value: "bullying" },
        { value: "claimedPayment" },
        { value: "allegedDamage" },
        { value: "writtenWarning" },
        { value: "termination" },
        { value: "itemsToReturn" },
        { value: "other" },
      ],
    },
  ];

  const createdFields: FormElementPropsWithoutProduct<EmploymentContract>[] = [
    ...personFields({ path: `${product.productClassName}.client`, showDiverseGenderOption: true }),
    {
      type: "ExpandableTextField",
      path: `${product.productClassName}.personalTextMessage`,
      isDisabled: () => true,
      additionalProps: {
        multiline: true,
      },
    },
    ...employmentContractSpecificFields,
  ];

  const dataCompletedFields: FormElementPropsWithoutProduct<EmploymentContract>[] = [
    ...employmentContractSpecificFields,
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.caseCategory`,
      isDisabled: ({ product }) => product.caseCategory === "advisory",
      options: ({ product }) => {
        return product.caseCategory === "advisory"
          ? [{ value: "enforceClaim" }, { value: "defendClaim" }, { value: "advisory" }]
          : [{ value: "enforceClaim" }, { value: "defendClaim" }];
      },

      alert: {
        onChange:
          "Das Ändern der Position der Mandantschaft (Klägerin/Beklagte) führt dazu, dass sich viele " +
          "Felder ändern und auch Felder verschwinden. Die neuen Felder müssen dann von Ihnen neu " +
          "befüllt werden, damit der Fall weiter bearbeitet werden kann. Möchten Sie die Position " +
          "der Mandantschaft im Verfahren tatsächlich ändern?",
      },
    },
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.defenseProcessStatus`,
      isDisabled: ({ product }) => product.stage! >= stagesCase.STAGE_DATA_COMPLETED_LAWFIRM,
      isHidden: ({ product }) => product.caseCategory !== "defendClaim",
      options: () => [
        { value: "reminder" },
        { value: "debtCollection" },
        { value: "paymentOrder" },
        { value: "enforcementOrder" },
        { value: "lawsuit" },
        { value: "none" },
      ],
    },
    {
      type: "ExpandableTextField",
      path: `${product.productClassName}.personalTextMessage`,
      isDisabled: () => true,
      additionalProps: {
        multiline: true,
      },
    },
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.paymentOrderDate`,
      validators: dateFieldDefault,
      isHidden: ({ product }) => product.caseCategory !== "defendClaim",
    },
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.enforcementOrderDate`,
      validators: dateFieldDefault,
      isHidden: ({ product }) => product.caseCategory !== "defendClaim",
    },
    ...abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_DATA_COMPLETED],
  ];

  const dataLawFirmCompletedFields: FormElementPropsWithoutProduct<EmploymentContract>[] = [...dataCompletedFields];

  const checkedFields: FormElementPropsWithoutProduct<EmploymentContract>[] = [
    ...employmentContractSpecificFields,
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.caseCategory`,
      isDisabled: ({ product }) => product.caseCategory === "advisory",
      options: ({ product }) => {
        return product.caseCategory === "advisory"
          ? [{ value: "enforceClaim" }, { value: "defendClaim" }, { value: "advisory" }]
          : [{ value: "enforceClaim" }, { value: "defendClaim" }];
      },
      alert: {
        onChange:
          "Das Ändern der Position der Mandantschaft (Klägerin/Beklagte) führt dazu, dass sich viele " +
          "Felder ändern und auch Felder verschwinden. Die neuen Felder müssen dann von Ihnen neu " +
          "befüllt werden, damit der Fall weiter bearbeitet werden kann. Möchten Sie die Position " +
          "der Mandantschaf im Verfahren tatsächlich ändern?",
      },
    },
    {
      type: "ExpandableTextField",
      path: `${product.productClassName}.personalTextMessage`,
      isDisabled: () => true,
      additionalProps: {
        multiline: true,
      },
    },
    ...abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_CHECKED],
  ];

  const inProgressFields: FormElementPropsWithoutProduct<EmploymentContract>[] = [
    ...employmentContractSpecificFields,
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.caseCategory`,
      isDisabled: ({ product }) => product.caseCategory === "advisory",
      options: ({ product }) => {
        return product.caseCategory === "advisory"
          ? [{ value: "enforceClaim" }, { value: "defendClaim" }, { value: "advisory" }]
          : [{ value: "enforceClaim" }, { value: "defendClaim" }];
      },
      alert: {
        onChange:
          "Das Ändern der Position  der Mandantschaft (Klägerin/Beklagte) führt dazu, dass sich viele " +
          "Felder ändern und auch Felder verschwinden. Die neuen Felder müssen dann von Ihnen neu " +
          "befüllt werden, damit der Fall weiter bearbeitet werden kann. Möchten Sie die Position " +
          "der Mandantschaf im Verfahren tatsächlich ändern?",
      },
    },
    ...abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_IN_PROGRESS],
  ];

  const communicationSentFields: FormElementPropsWithoutProduct<EmploymentContract>[] = [
    ...employmentContractSpecificFields,
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.hasMonitoringBilling`,
      isHidden: ({ product }: { product: ProductUnion }) =>
        !_.includes([2], product.caseInsurance.insurer?.id) || product.productClassName === "dataLeakContract", // TODO: Is this correct in employment contract
      options: () => trueFalseDefaultOptions(`${product.productClassName}.hasMonitoringBilling`),
    },
    ...abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_EXTRAJUDICIAL_COMMUNICATION_SENT],
  ];

  const reactionReceivedFields: FormElementPropsWithoutProduct<EmploymentContract>[] = [
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.hasMonitoringBilling`,
      isHidden: ({ product }: { product: ProductUnion }) =>
        !_.includes([2], product.caseInsurance.insurer?.id) || product.productClassName === "dataLeakContract", // TODO: Is this correct in employment contract
      options: () => trueFalseDefaultOptions(`${product.productClassName}.hasMonitoringBilling`),
    },
    ...abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_EXTRAJUDICIAL_REACTION_RECEIVED],
  ];

  const prepareLawsuitFields: FormElementPropsWithoutProduct<EmploymentContract>[] = [
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.hasMonitoringBilling`,
      isHidden: ({ product }: { product: ProductUnion }) =>
        !_.includes([2], product.caseInsurance.insurer?.id) || product.productClassName === "dataLeakContract", // TODO: Is this correct in employment contract
      options: () => trueFalseDefaultOptions(`${product.productClassName}.hasMonitoringBilling`),
    },
    ...abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_PREPARE_LAWSUIT],
  ];

  // gets fields from abstractLitigation and overwrites principalClaim and "returns"
  // overwritten/updated lawsuitFields
  const lawsuitFields: FormElementPropsWithoutProduct<EmploymentContract>[] = overwriteFormElementField(
    [...abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_LAWSUIT]],
    `${product.productClassName}.principalClaim`,
    {
      isHidden: ({ product, values }) =>
        values[product.productClassName].caseCategory !== "enforceClaim" ||
        ["lawsuitRevocation", "lawsuitRevocationBeforeHearing"].includes(
          values[product.productClassName].lawsuitCompletedReason
        ),
    }
  );

  // gets fields from abstractLitigation and overwrites principalClaim and "returns"
  // overwritten/updated trialFields
  const trialFields: FormElementPropsWithoutProduct<EmploymentContract>[] = overwriteFormElementField(
    [...abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_TRIAL]],
    `${product.productClassName}.principalClaim`,
    {
      isHidden: ({ product, values }) =>
        values[product.productClassName].caseCategory !== "enforceClaim" ||
        ["lawsuitRevocation", "lawsuitRevocationBeforeHearing"].includes(
          values[product.productClassName].lawsuitCompletedReason
        ),
    }
  );

  const lawsuitCompletedFields: FormElementPropsWithoutProduct<EmploymentContract>[] = [
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.hasMonitoringBilling`,
      isHidden: ({ product }: { product: ProductUnion }) =>
        !_.includes([2], product.caseInsurance.insurer?.id) || product.productClassName === "dataLeakContract", // TODO: Is this correct in employment contract
      options: () => trueFalseDefaultOptions(`${product.productClassName}.hasMonitoringBilling`),
    },
    {
      type: "ValidatorTextField",
      path: `${product.productClassName}.courtReference`,
      validators: instituteReferenceDefault,
    },
    {
      type: "ValidatorDateField",
      validators: dateFieldDefault,
      path: `${product.productClassName}.judgementEebSentDate`,
    },
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.promulgationOfJudgementDate`,
      validators: dateFieldDefault,
    },
    ...abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_LAWSUIT_COMPLETED],
  ];

  const completedFields: FormElementPropsWithoutProduct<EmploymentContract>[] = [
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.hasMonitoringBilling`,
      isHidden: ({ product }: { product: ProductUnion }) =>
        !_.includes([2], product.caseInsurance.insurer?.id) || product.productClassName === "dataLeakContract", // TODO: Is this correct in employment contract
      options: () => trueFalseDefaultOptions(`${product.productClassName}.hasMonitoringBilling`),
      isDisabled: () => {
        return !currentUser || !userHasOneOfTheseRoles(currentUser, [Roles.accounting, Roles.admin]);
      },
    },
    ...abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_COMPLETED],
  ];

  return {
    [stagesCase.STAGE_CREATED]: createdFields,
    [stagesCase.STAGE_DATA_COMPLETED]: dataCompletedFields,
    [stagesCase.STAGE_WAITING_FOR_FEEDBACK]: dataCompletedFields,
    [stagesCase.STAGE_DATA_COMPLETED_LAWFIRM]: dataLawFirmCompletedFields,
    [stagesCase.STAGE_CHECKED]: checkedFields,
    [stagesCase.STAGE_IN_PROGRESS]: inProgressFields,
    [stagesCase.STAGE_EXTRAJUDICIAL_COMMUNICATION_SENT]: communicationSentFields,
    [stagesCase.STAGE_EXTRAJUDICIAL_REACTION_RECEIVED]: reactionReceivedFields,
    [stagesCase.STAGE_ACTION_REQUIRED]:
      abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_ACTION_REQUIRED],
    [stagesCase.STAGE_PREPARE_LAWSUIT]: prepareLawsuitFields,
    [stagesCase.STAGE_LAWSUIT]: lawsuitFields,
    [stagesCase.STAGE_TRIAL]: trialFields,
    [stagesCase.STAGE_EVALUATE_JUDGEMENT]:
      abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_EVALUATE_JUDGEMENT],
    [stagesCase.STAGE_LAWSUIT_COMPLETED]: lawsuitCompletedFields,
    [stagesCase.STAGE_COMPLETED]: completedFields,
  };
}
